import { Link } from "react-router-dom";
import { ArrowRight, Check, MessageSquare, Scale, FileText, HeartPulse, GraduationCap, Rocket } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { SERVICES } from "@/data/services";
import { HOUR_BLOCKS, HOURLY_RATE, VAT_SUFFIX, lowestForService, formatPrice } from "@/data/packages";

const ICONS = { MessageSquare, Scale, FileText, HeartPulse, GraduationCap, Rocket };

export default function Services() {
  return (
    <main data-testid="services-page">
      <Seo
        title="Fixed-Fee HR Services for UK SMEs, No Retainers | Electra HR"
        siteName="Electra HR"
        description="Six fixed-fee HR services for founders and SMEs of 5 to 50 people, grievances, contracts, health checks and more. Same-day answers, no retainer required."
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: window.location.origin },
            { "@type": "ListItem", position: 2, name: "Services", item: `${window.location.origin}/services` },
          ],
        }}
      />
      <section className="grain bg-ink pb-16 pt-32 sm:pt-40 text-paper">
        <div className="container-x">
          <p className="eyebrow text-pink">Services</p>
          <h1 className="mt-5 max-w-3xl font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05]">
            Everything an SME needs. Nothing a corporate would sell you.
          </h1>
          <p className="mt-6 max-w-2xl text-base sm:text-lg leading-relaxed text-paper/65">
            Six services, all fixed-fee or blocks of hours, with prices on the page. No retainer, no monthly fee.
          </p>
        </div>
      </section>
      <section className="bg-paper py-16 sm:py-24">
        <div className="container-x grid grid-cols-1 md:grid-cols-2 gap-6">
          {SERVICES.map((s, i) => {
            const Icon = ICONS[s.icon];
            const low = lowestForService(s.slug);
            return (
              <Reveal key={s.slug} delay={(i % 2) * 0.08}>
                <Link
                  to={`/services/${s.slug}`}
                  data-testid={`services-page-card-${s.slug}`}
                  className="group flex h-full flex-col border-2 border-ink bg-paper p-8 sm:p-10 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[8px_8px_0_0_#0A0E17]"
                >
                  <div className="flex items-center justify-between">
                    <Icon size={28} className="text-ink" strokeWidth={1.8} />
                    <span className="font-mono text-xs text-ink/35">0{i + 1}</span>
                  </div>
                  <h2 className="mt-6 font-display text-xl sm:text-2xl font-semibold tracking-tight">{s.title}</h2>
                  <p className="mt-2 text-base font-medium text-ink/60">{s.tagline}</p>
                  <p className="mt-4 text-sm leading-relaxed text-ink/60 line-clamp-3">{s.problem}</p>
                  {low ? (
                    <p data-testid={`services-page-price-${s.slug}`} className="mt-4 font-display text-lg font-semibold text-ink">
                      {formatPrice(low)}
                    </p>
                  ) : null}
                  <span className="mt-auto pt-6 flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-ink/45 group-hover:text-ink">
                    Explore <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </span>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </section>
      <section className="bg-paper pb-16 sm:pb-24">
        <div className="container-x">
          <Reveal>
            <Link
              to="/legal-privilege-and-hr-advice"
              data-testid="services-privilege-guide-link"
              className="group flex flex-col gap-4 border-2 border-ink bg-pink/10 p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-[6px_6px_0_0_#0F0F1A] sm:flex-row sm:items-center sm:justify-between"
            >
              <div>
                <p className="eyebrow text-ink/50">Legal protection</p>
                <h2 className="mt-2 font-display text-xl sm:text-2xl font-semibold tracking-tight">Is your HR advice legally privileged?</h2>
                <p className="mt-2 max-w-2xl text-sm sm:text-base leading-relaxed text-ink/65">
                  Usually not. Learn what is protected, what is not, and when a matter should move from HR into Bonsai Law.
                </p>
              </div>
              <span className="inline-flex shrink-0 items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-ink/50 group-hover:text-ink">
                Read the guide <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
              </span>
            </Link>
          </Reveal>
        </div>
      </section>
      <section id="pricing" data-testid="pricing-section" className="bg-ink py-16 sm:py-24 text-paper">
        <div className="container-x">
          <Reveal>
            <p className="eyebrow text-pink">Pricing, honestly</p>
            <h2 className="mt-4 max-w-2xl font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight">
              Use us as much or as little as you need. No monthly fee.
            </h2>
            <p className="mt-5 max-w-2xl text-base sm:text-lg leading-relaxed text-paper/65">
              A fixed monthly retainer means you either pay for help you don't use, or ration help you
              need. We don't do that. Buy a block of hours up front and use it within 12 months of
              purchase, as things come up. We record the time and keep you updated, so there are no
              surprises. £{HOURLY_RATE} an hour{VAT_SUFFIX}, and the more hours you buy, the bigger the discount.
            </p>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {HOUR_BLOCKS.map((b, i) => (
              <Reveal key={b.hours} delay={i * 0.08}>
                <div data-testid={`pricing-block-${b.hours}`} className="group flex h-full flex-col border border-white/10 bg-ink-soft p-8 transition-all duration-500 hover:-translate-y-1.5 hover:border-pink/60">
                  <p className="font-display text-5xl font-semibold tracking-tight">
                    {b.hours}<span className="text-pink">hrs</span>
                  </p>
                  {b.saving ? <p className="mt-2 font-mono text-xs uppercase tracking-[0.2em] text-pink">{b.saving}</p> : null}
                  {b.price ? (
                    <p data-testid={`pricing-price-${b.hours}`} className="mt-3 font-display text-2xl font-semibold text-pink">£{b.price.toLocaleString("en-GB")}{VAT_SUFFIX}</p>
                  ) : null}
                  <p className="mt-4 text-sm leading-relaxed text-paper/60">{b.use}</p>
                  <ul className="mt-6 space-y-2.5 text-sm text-paper/60">
                    <li className="flex gap-2.5"><Check size={17} className="mt-0.5 shrink-0 text-pink" /> Use within 12 months of purchase, as things come up</li>
                    <li className="flex gap-2.5"><Check size={17} className="mt-0.5 shrink-0 text-pink" /> Time recorded, usage updates, no surprises</li>
                    <li className="flex gap-2.5"><Check size={17} className="mt-0.5 shrink-0 text-pink" /> Top up whenever you're running low</li>
                  </ul>
                  <Link to={`/contact?need=${b.id}`} data-testid={`pricing-quote-${b.hours}`} className="mt-auto pt-7">
                    <span className="group/btn flex items-center justify-center gap-2 border-2 border-pink px-5 py-3 text-sm font-bold text-pink transition-colors hover:bg-pink hover:text-ink">
                      Get started <ArrowRight size={16} />
                    </span>
                  </Link>
                </div>
              </Reveal>
            ))}
          </div>
          <p data-testid="pricing-blocks-scope" className="mt-6 max-w-3xl text-sm leading-relaxed text-paper/55">
            Blocks cover run-of-the-mill, day-to-day HR: advice, letters, meetings, absence, conduct and performance.
            Defined projects, such as a staff handbook, an independent investigation or a redundancy process, are
            priced separately at a fixed fee, shown on each service page.
          </p>
          <Reveal delay={0.1}>
            <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-6 border-2 border-pink bg-pink/10 p-7 sm:p-8">
              <div className="flex-1">
                <p className="font-display text-xl font-semibold">Not sure what you need? That's fine.</p>
                <p className="mt-2 text-sm sm:text-base leading-relaxed text-paper/65">
                  Tell us what's going on in a few lines. An HR adviser will reply with an honest view of
                  what makes sense and a fixed price within one working day, by email or on a call with
                  the adviser if you prefer. No pressure, no hard sell.
                </p>
              </div>
              <Link to="/contact?need=other" data-testid="pricing-free-consult-cta" className="group flex shrink-0 items-center justify-center gap-2 bg-pink px-7 py-4 font-bold text-ink transition-transform duration-300 hover:-translate-y-0.5">
                Get a fixed quote <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
